import { Box, HStack, Heading, Text } from '@medely/ui-kit';
import { Button, Dialog, DialogProps } from '@medely/web-components';
import { Calendar, CalendarClock, Clock, MapMarkerAlt } from '@medely/web-components/icons';
import React from 'react';
import { CovidProtocol, JobDetailsListItem, ProfessionalTerms, ShiftDate, ShiftLocation } from '..';
import { ExtendedRateInfo } from '@mp/components/shared/ExtendedRateInfo';
import { COVID_REQUIREMENTS, gateNames } from '../../../constants';
import { OFFER_SOURCE } from '../../../constants/analytics';
import DisplayHelpers from '../../../utils/displayHelpers';
import { IPreBookConfirmationProps } from './interface';
import { HideFrom } from '../../HideFrom';
import { useGate } from '@mp/hooks';
import { parseFragmentData } from '@mp/graphql/generated';
import { JobDetailsFragment } from '../jobDetailsComponents/graphql';

type IPreBookConfirmationDialogProps = DialogProps & IPreBookConfirmationProps;

export const PreBookConfirmationDialog = ({
  open,
  onClose = () => null,
  job: inputJob,
  bookJob,
  bookingLoading,
  bookJobWithAttestation,
  bookAndAttestLoading,
  userCoordinates,
  onboarding = false,
  disabled = false,
}: IPreBookConfirmationDialogProps) => {
  const job = parseFragmentData(JobDetailsFragment, inputJob);

  const showXRInfo = useGate(gateNames.xrTransparency);

  const covidText =
    COVID_REQUIREMENTS.confirmation[
      job?.location?.covid_requirement as keyof typeof COVID_REQUIREMENTS.confirmation
    ] ?? '';

  const handleBookButtonAction = () => {
    onboarding
      ? bookJobWithAttestation(
          { jobId: job?.id as number, attest: true, offer_source: OFFER_SOURCE.o },
          {
            onSuccess: () => {
              onClose();
            },
          },
        )
      : bookJob(job?.id, {
          onSuccess: () => {
            onClose();
          },
        });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Review shift details"
      data-testid="book-job-confirmation-dialog"
      maxWidth="sm"
      fullWidth
      content={
        <Box mb={3}>
          <Box py={1.5}>
            <JobDetailsListItem icon={<MapMarkerAlt />} title="Location">
              <ShiftLocation
                job={inputJob}
                bookVariant="pre-book"
                distanceVariant="local"
                userCoordinates={userCoordinates}
              />
            </JobDetailsListItem>
          </Box>
          <Box py={1}>
            <JobDetailsListItem icon={<Calendar />} title="Date">
              <ShiftDate job={inputJob} displayStartingSoonBadge />
            </JobDetailsListItem>
          </Box>
          <Box py={1}>
            <JobDetailsListItem icon={<Clock />} title="Shift time">
              <Text size="m">
                {DisplayHelpers.job.time(
                  job?.current_ends_time,
                  job?.current_starts_time,
                  job?.approved_clock_in,
                  job?.approved_clock_out,
                  job?.location?.timezone_lookup,
                  job?.guaranteed_minimum_hours,
                  job?.status,
                  { scheduledShiftStyle: true },
                )}
              </Text>
            </JobDetailsListItem>
          </Box>
          <HideFrom saas agency>
            {showXRInfo && (
              <Box py={1}>
                <ExtendedRateInfo icon={<CalendarClock />} job={inputJob} />
              </Box>
            )}
          </HideFrom>
          <HideFrom saas>
            <Box py={2}>
              <ProfessionalTerms />
            </Box>
          </HideFrom>
          {!!job?.location?.covid_requirement && !!covidText && (
            <CovidProtocol description={covidText} />
          )}
          {job?.detail && (
            <Box py={1}>
              <Heading size="s">Additional details</Heading>
              <Box pt={1.5}>
                <Text size="m">{job?.detail}</Text>
              </Box>
            </Box>
          )}
        </Box>
      }
      actions={
        <HStack width="100%">
          <Button
            color="primary"
            variant="outlined"
            sx={{ width: '50%', mr: 1 }}
            onClick={onClose}
            data-testid="book-job-confirmation-dialog-close-button"
          >
            Close
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={bookingLoading || bookAndAttestLoading || disabled}
            loading={bookingLoading || bookAndAttestLoading}
            sx={{ width: '50%' }}
            onClick={handleBookButtonAction}
            data-testid="book-job-confirmation-dialog-book-button"
          >
            Book shift
          </Button>
        </HStack>
      }
    />
  );
};
